// Old paths keep working (SIGNED_IN_PLAN section 4), and sign-in lands
// where SI-14 says. Pure: the router calls these, tests/w1-redirects.test
// pins them.

const first = (v) => (Array.isArray(v) ? v[0] : v);

/**
 * Where an old or signed-in-only path goes instead, or null to stay.
 *   to  { path, params, query }
 *   me  the signed-in username, or null
 */
export function redirectFor(to, { me = null } = {}) {
  const path = (to && to.path) || "/";
  const query = (to && to.query) || {};

  if (path === "/" && me) return { path: "/sessions/mine", replace: true };
  if (path === "/sessions") return { path: "/sessions/mine", replace: true };

  if (path === "/my-audio") {
    const tab = first(query.tab);
    if (tab === "favorites") return { path: "/library/favorites", replace: true };
    if (tab === "purchased") return { path: "/library/my-audio", query: { tab: "purchased" }, replace: true };
    if (tab === "mine") return { path: "/library/my-audio", query: { tab: "created" }, replace: true };
    return { path: "/library/my-audio", replace: true };
  }

  if (path === "/license-studio") return { path: "/library/my-audio", replace: true };

  const storage = /^\/users\/([^/]+)\/storage\/?$/.exec(path);
  if (storage) {
    const who = decodeURIComponent(storage[1]);
    return me && who === me
      ? { path: "/settings/storage", replace: true }
      : { path: `/users/${encodeURIComponent(who)}`, replace: true };
  }

  // The Google Play account-deletion URL: signed out it explains; signed in
  // it opens the delete modal in Settings › Account.
  if (path === "/delete-account" && me) {
    return { path: "/settings/account", query: { delete: "1" }, replace: true };
  }

  return null;
}

// A redirect that is safe to follow: an in-app path, never another origin.
export function safeRedirect(value) {
  const r = first(value);
  return typeof r === "string" && r.startsWith("/") && !r.startsWith("//") ? r : null;
}

/**
 * Where log in or sign up lands (SI-14), in order:
 *   1. a pending invite goes to /invite/<token>
 *   2. a ?redirect= that starts with /
 *   3. log in only: /get-started when the public channel is empty
 *   4. /sessions/mine
 */
export function landingAfterAuth({ pendingInvite = null, redirect = null, publicEmpty = false, form = "login" } = {}) {
  if (pendingInvite) return `/invite/${pendingInvite}`;
  const r = safeRedirect(redirect);
  if (r) return r;
  if (form === "login" && publicEmpty) return "/get-started";
  return "/sessions/mine";
}

/**
 * Where signing out leaves the page, or null to stay. A page that needs an
 * account goes to Welcome; when the session expired under it (a 401 from
 * the API, a refused socket), to Log in instead, coming back here after
 * (L11). A public page stays either way.
 *   route  { path, fullPath, name, meta }
 */
export function afterSignOut(route, { expired = false } = {}) {
  if (!route) return null;
  if (route.meta && route.meta.requiresAuth === false) return null;
  if (expired && route.name !== "Login") {
    return { name: "Login", query: { redirect: route.fullPath || route.path || "/" } };
  }
  return route.path !== "/" ? { path: "/" } : null;
}

// Same path, only the query changed (Search's typing, a filter chip): the
// guard needn't check the token again.
export function isQueryOnly(to, from) {
  return !!to && !!from && to.path === from.path && to.fullPath !== from.fullPath && from.matched?.length > 0;
}
